//Dependencies
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFilterKeyboardShortcuts } from '../../../../hooks/useFilterKeyboardShortcuts';
import { useTableCurrentPage } from '../../../../hooks/useTableCurrentPage';
import {
  createColumnHelper,
  ColumnFiltersState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
  PaginationState,
  VisibilityState,
  FilterFnOption,
  Row,
  RowSelectionState,
} from '@tanstack/react-table';
import { format, formatISO, parseISO } from 'date-fns';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';

// Double Tap Support
import { DoubleTapRow } from '../components/DoubleTapRow';
import { SortableHeader } from '../components/SortableHeader';

// Styled Components
import {
  TrStyled,
  TableContainerStyled,
  SubContainerFiltersStyled,
  TextCellStyled,
  SubContainerHead,
  ShowFiltersButton,
  ArrowBottomStyled,
  ContainerButtonStyled,
  ContainerStyled,
  ContainerLoader,
  FiltersWithLoadingStyled,
} from './styles';
import {
  Th,
  Tbody,
  Td,
  Title,
  Table,
  TextHeader,
  InputCheckContainer,
  CheckBoxStyled,
  DropDownInputStyled,
  SearchableDropdownStyled,
  MultiSelectDropdownStyled,
  InputCalendarStyled,
  MultiLineCell,
} from '../components/MainStyledComponents';

// Components
import { CheckBox } from '../../../inputs/CheckBox';
import { PkCell } from '../components/PkCell';
import { PaginationButtons } from '../components/PaginationButtons';
import { SelectionBanner } from '../components/SelectionBanner';
import InputExpedient from '../../../inputs/InputExpedient';
import InputIndex from '../../../inputs/InputIndex';
import InputText from '../../../inputs/InputText';
import InputPK from '../../../inputs/InputPK';
import { DropDownVisibilityColumn } from '../components/DropDownVisibilityColumn';
import { createFlagColumn, exportIgnoreProps } from '../components/flagColumn';
import { useFlagScopeTexts } from '../../../../hooks/useFlagScopeTexts';
import { SpecialTag } from '../../SpecialTag';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';

// Types
import { columnID, getColumnNames, getColumnId } from '../components/types';
import { tableProps, dataTable, flagRowType } from './types';
import {
  onChangeValue,
  Options,
} from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';
import {
  ImperativeRefCalendarInput,
  onChangeInputType,
} from '../../../inputs/Calendar/types';
import {
  ImperativeRefInputExpedient,
  onChangeInputExpedientTypes,
} from '../../../inputs/InputExpedient/types';
import {
  ImperativeRefInputIndex,
  onChangeInputIndexTypes,
} from '../../../inputs/InputIndex/types';
import {
  ImperativeRefInputPK,
  onChangeInputPKTypes,
} from '../../../inputs/InputPK/types';
import { variant, size } from '../../SpecialTag/types';
import { Order, OrderType } from '../../../../types/orders';
import { getTrackOptions } from '../../../../utils/catalogLabels';
import { Role } from '../../../../types/auth';
import { takeBase, tableIds } from '../types';
import { option } from '../InputRadioArrowTable/types';
import { useServerPageSize } from '../utils/useServerPageSize';
import { useMirroredCursor } from '../utils/useMirroredCursor';

// Mocks
import { mocksTable } from './mocks';

// Utils
import { roidFilterFunction, dateFilterFunction } from './utils';
import { formatRoidForDisplay } from '../utils/roidFormatter';

// Feature Flags
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';

// Media
import {
  useIsLandscapeTablet,
  useIsPortraitTablet,
  useIsSmallDesktop,
} from '../../../../hooks/useMediaBreakPoints';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../../inputs/DropDown/MultiSelectDropdown';
import { ImperativeRefMultiSelect } from '../../../inputs/DropDown/MultiSelectDropdown/types';
import IncludeInactiveToggle from '../../../inputs/IncludeInactiveToggle';
import {
  filterAndSortMultimediaUsers,
  filterAndSortOperators,
} from '../../../../utils/multimediaUserFilters';
import SearchableDropdown from '../../../inputs/SearchableDropdown';
import { getStateOptionsByRoleAndEntityType } from '../../../../utils/order';
import { TABLE_ENTITY_TYPES, listingFiltersByRole } from '../types';

// GQL
import { client } from '../../../../gql/client';
import {
  GET_SUB_CAPITOLS_BY_IDS,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
} from '../../../../gql/queries/orders/predefinedData';

// Redux
import {
  getCursor,
  getIsLoadingData,
} from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { Loader } from '../../../inputs/Loader';

// Columnas ordenables server-side → campo del backend (whitelist GenericOrder)
const SORTABLE_COLUMNS = {
  state: 'state',
  registerInitDate: 'registerInitDate',
  creationDate: 'creationDate',
  pkInit: 'pkInitKm',
  pkEnd: 'pkEndKm',
  track: 'track',
  direction: 'direction',
  // Solo se renderiza en históricos RV (showRouteNameColumn); el backend RV
  // tiene 'name' en SURVEILLANCE_ROUTE_SORTABLE_FIELDS.
  name: 'name',
  // Solo ordenable en RV (SURVEILLANCE_ROUTE_SORTABLE_FIELDS incluye
  // registerEndDate): la columna visual sigue siendo derivedEndDate — el
  // adapter RV mapea derivedEndDate = order.registerEndDate, misma cosa.
  // En tablas NO-RV la columna mantiene header plano (ver `columns`), así
  // que este mapeo solo entra en juego con tableId de RV.
  derivedEndDate: 'registerEndDate',
} as const;
const DEFAULT_SORT_FIELD = 'registerInitDate';

/**
 * Por defecto la bandera marca la Order de la fila: las filas del histórico de
 * OT ya llevan su id (`selectAllOrders` → `order.id`). Fuera del componente para
 * que la referencia sea estable y no rehaga las columnas en cada render.
 */
const defaultGetFlagOrderId = (row: flagRowType): string | undefined =>
  row.id || undefined;

export const CheckBoxTagTable = ({
  idCss,
  titleText,
  data,
  onReFetchData,
  rowState,
  setRowState,
  tableId,
  onReFetchPagination,
  selectAllMode = false,
  excludedIds,
  onSelectAll,
  onClearSelection,
  rowSelection: propsRowSelection,
  onRowSelectionChange: propsOnRowSelectionChange,
  selectedCount: propsSelectedCount,
  hiddenColumnIds,
  hiddenFilterIds,
  showRouteNameColumn,
  showCreatorColumn,
  showOperatorsFilter,
  onRowDoubleTap,
  // Fuera de `props`: no son atributos del <table> (la tabla hace
  // <Table {...props}>).
  showFlagColumn = false,
  showOnlyFlaggedFilter = false,
  getFlagOrderId = defaultGetFlagOrderId,
  ...props
}: tableProps): JSX.Element => {
  // V4 FR-1.5 — helper para condicionar el render de cada filtro por id.
  const isFilterHidden = (id: string): boolean =>
    hiddenFilterIds?.includes(id) ?? false;
  // Controlled mode: parent provides rowSelection via usePersistentSelection hook
  const isControlled = propsRowSelection !== undefined;
  const { t } = useTranslation(['tables', 'common', 'forms']);
  const flagScopeTexts = useFlagScopeTexts();
  const dataAll = data?.genericOrders ?? mocksTable;
  const roleActive = data?.roleActive;

  const rowStateRef = useRef(rowState);
  useEffect(() => {
    rowStateRef.current = rowState;
  }, [rowState]);
  const excludedIdsRef = useRef(excludedIds);
  useEffect(() => {
    excludedIdsRef.current = excludedIds;
  }, [excludedIds]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const globalCursor = useSelector(getCursor(tableId as string), isEqual);
  const isLoadingData = useSelector(getIsLoadingData(tableId), isEqual);
  const inputListConRef = useRef<ImperativeRefInput>(null);
  const inputListTeamRef = useRef<ImperativeRefMultiSelect>(null);
  const inputListCapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListSubcapitolRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListClassificationRef =
    useRef<ImperativeRefSearchableDropdown>(null);
  const inputListTrackRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputListRoadRef = useRef<ImperativeRefSearchableDropdown>(null);
  const inputStateRef = useRef<ImperativeRefMultiSelect>(null);
  const inputUserRef = useRef<ImperativeRefMultiSelect>(null);
  const inputOperatorsRef = useRef<ImperativeRefMultiSelect>(null);
  const inputDateRef = useRef<ImperativeRefCalendarInput>(null);
  const inputExpedientRef = useRef<ImperativeRefInputExpedient>(null);
  const inputIndexRef = useRef<ImperativeRefInputIndex>(null);
  const inputPKInitRef = useRef<ImperativeRefInputPK>(null);
  const inputPKEndRef = useRef<ImperativeRefInputPK>(null);
  const inputListPredefinedRef = useRef<ImperativeRefSearchableDropdown>(null);

  // Feature flags for filter/column visibility
  const showCapitolFilter = useFeatureFlag('filter_show_capitol', true);
  const showSubCapitolFilter = useFeatureFlag('filter_show_subcapitol', true);
  const showPredefinedFilter = useFeatureFlag('filter_show_predefined', true);

  const isSmallDesktop = useIsSmallDesktop();
  const isLanscapeTablet = useIsLandscapeTablet();
  const isPortraitTablet = useIsPortraitTablet();

  const [expedientDefault, setExpedientDefault] = useState<number | undefined>(
    undefined
  );
  const [indexDefault, setIndexDefault] = useState<number | undefined>(
    undefined
  );
  const [showFilters, setShowFilters] = useState(false);
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const rowSelection = isControlled ? propsRowSelection! : internalRowSelection;
  const setRowSelection = isControlled
    ? propsOnRowSelectionChange!
    : setInternalRowSelection;
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  // C-11.7: persistido en Redux para mantener coherencia paginador↔cursor
  // al navegar a un detail y volver. Ver hooks/useTableCurrentPage.
  const [currentServerPage, setCurrentServerPage] = useTableCurrentPage(
    tableId as string
  );
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: takeBase,
  });

  const initialSubCapitolsOptions = data?.filtersDataOptions?.subCapitols?.map(
    (subCapitol: { name: string; id: string }) => ({
      label: subCapitol.name,
      value: subCapitol.id,
    })
  );

  const [subCapitolOptions, setSubCapitolOptions] = useState(
    initialSubCapitolsOptions
  );

  const [selectorCursor, setSelectorCursor] = useMirroredCursor<{
    description: string | undefined; // filtro por descripción (texto libre)
    take: number;
    cursor: string;
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat with other tables sharing this cursor shape)
    states: string[] | undefined; // multi-select (Plan 2 Tarea: filtro estado multi-select)
    userIds: string[] | undefined; // multi-select creador
    operatorIds: string[] | undefined; // multi-select operarios (solo RV: SurveillanceRoute.userIDs)
    includeInactive: boolean | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    routeName: string | undefined; // filtro "Nombre del recorrido" (RV, FR-2.3)
    roadId: string | undefined; // filtro "Carretera" (RV, FR-2.3)
    sortField: string | undefined;
    orderBy: 'asc' | 'desc' | undefined;
    onlyFlagged: true | undefined; // «Solo marcadas»: true o undefined, nunca false
  }>(globalCursor);

  const [selectorData, setSelectorData] = useState<{
    description: string | undefined; // filtro por descripción (texto libre)
    indexType: number | undefined;
    indexExpedition: number | undefined;
    concessionId: string | undefined;
    teamIds: string[] | undefined;
    fromDate: string | undefined;
    toDate: string | undefined;
    classification: string | undefined;
    track: string | undefined;
    capitolId: string | undefined;
    subCapitolId: string | undefined;
    predefinedOrderId: string | undefined;
    state: string | undefined; // legacy single (kept for back-compat)
    states: string[] | undefined; // multi-select
    userIds: string[] | undefined; // multi-select creador
    operatorIds: string[] | undefined; // multi-select operarios (solo RV)
    includeInactive: boolean | undefined;
    pkInitKm: number | undefined;
    pkInitMeter: number | undefined;
    pkEndKm: number | undefined;
    pkEndMeter: number | undefined;
    routeName: string | undefined; // filtro "Nombre del recorrido" (RV, FR-2.3)
    roadId: string | undefined; // filtro "Carretera" (RV, FR-2.3)
  }>({
    description: undefined,
    indexType: undefined,
    indexExpedition: undefined,
    concessionId: undefined,
    teamIds: undefined,
    fromDate: undefined,
    toDate: undefined,
    classification: undefined,
    track: undefined,
    capitolId: undefined,
    subCapitolId: undefined,
    predefinedOrderId: undefined,
    state: undefined,
    states: undefined,
    userIds: undefined,
    operatorIds: undefined,
    includeInactive: undefined,
    pkInitKm: undefined,
    pkInitMeter: undefined,
    pkEndKm: undefined,
    pkEndMeter: undefined,
    routeName: undefined,
    roadId: undefined,
  });

  // Tarea #11 — page-size selector. Solo activo en el listado principal de
  // Work Orders historical (`SEARCH_AND_CREATE_HISTORICAL_WORKORDERS`).
  // `liveFilters: selectorData` para respetar filtros del form aún no
  // comprometidos a Redux al cambiar el take.
  const { takePagination, paginationProps } = useServerPageSize({
    tableId: tableId as string,
    // Tour P2 (2026-07-06): históricos RV es un listado server-side de pleno
    // derecho — page-size selector activo también ahí.
    enabled:
      tableId === tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS ||
      tableId === tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES,
    selectorCursor,
    liveFilters: selectorData as unknown as Record<string, unknown>,
    setCurrentServerPage,
    onReFetchData,
  });

  const handleClick = useCallback(() => {
    setShowFilters(!showFilters);
  }, [showFilters]);

  useEffect(() => {
    isLanscapeTablet ? setShowFilters(false) : setShowFilters(true);
  }, [isLanscapeTablet]);

  // Auto-select concession if there's only one
  const hasAppliedDefaultConcession = useRef(false);
  useEffect(() => {
    const concessions = data?.filtersDataOptions?.concessions;
    if (
      concessions &&
      concessions.length === 1 &&
      !hasAppliedDefaultConcession.current &&
      !globalCursor?.concessionId
    ) {
      hasAppliedDefaultConcession.current = true;
      const concessionId = concessions[0].id;
      const dataCursor = {
        ...globalCursor,
        concessionId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        concessionId,
      }));
    }
  }, [
    data?.filtersDataOptions?.concessions,
    globalCursor?.concessionId,
    tableId,
    dispatch,
  ]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Feature flag column visibility
  useEffect(() => {
    setColumnVisibility((prev) => ({
      ...prev,
      predefinedOrder: showPredefinedFilter,
      capitol: showCapitolFilter,
      subCapitol: showSubCapitolFilter,
    }));
  }, [showPredefinedFilter, showCapitolFilter, showSubCapitolFilter]);

  useEffect(() => {
    const concessions = data?.filtersDataOptions?.concessions;
    const teams = data?.filtersDataOptions?.teams;
    setColumnVisibility((prev) => ({
      ...prev,
      concession: !concessions || concessions.length > 1,
      team: !!teams && teams.length > 0,
    }));
  }, [data?.filtersDataOptions?.concessions, data?.filtersDataOptions?.teams]);

  const [sizeTag, setSizeTag] = useState<size>(
    isPortraitTablet ? 'circle' : 'small'
  );

  useLayoutEffect(() => {
    setSizeTag(isPortraitTablet ? 'circle' : 'small');
  }, [isPortraitTablet]);

  const callbackSetRoid = useCallback(async () => {
    let classification;
    if (selectorCursor?.classification) {
      const classificationRaw = await client.query({
        query: GET_CLASSIFICATION_BY_ID_OR_ROID,
        variables: {
          id: selectorCursor?.classification,
        },
      });
      classification =
        classificationRaw?.data?.classification?.classificationCode;
    }
    // Actualizar valores por defecto desde el cursor
    setExpedientDefault(
      selectorCursor?.indexExpedition !== undefined &&
        selectorCursor?.indexExpedition >= 0
        ? selectorCursor.indexExpedition
        : undefined
    );
    setIndexDefault(
      selectorCursor?.indexType !== undefined && selectorCursor?.indexType >= 0
        ? selectorCursor.indexType
        : undefined
    );
  }, [selectorCursor]);

  useEffect(() => {
    callbackSetRoid();
  }, [selectorCursor]);

  const columnHelper = createColumnHelper<dataTable>();
  const translatedColumnNames = useMemo(() => getColumnNames(t), [t]);

  // 2026-07-17: Fecha final (derivedEndDate) ordenable SOLO en RV — el
  // backend solo whitelistea `registerEndDate` en
  // SURVEILLANCE_ROUTE_SORTABLE_FIELDS (en genéricas/campañas derivedEndDate
  // es derivado y NO ordenable).
  const isSurveillanceTable =
    TABLE_ENTITY_TYPES[tableId as string] === OrderType.SurveillanceRoute;
  // Ordenación server-side: campo activo y dirección derivados del cursor Redux
  const activeSortField = selectorCursor?.sortField ?? DEFAULT_SORT_FIELD;
  const activeSortColumnId =
    (Object.keys(SORTABLE_COLUMNS) as (keyof typeof SORTABLE_COLUMNS)[]).find(
      (k) => SORTABLE_COLUMNS[k] === activeSortField
    ) ?? 'registerInitDate';
  const sortDirection: 'asc' | 'desc' =
    selectorCursor?.orderBy === 'asc' ? 'asc' : 'desc';

  const onSortColumn = useCallback(
    (sortField: string) => {
      const isActive = activeSortField === sortField;
      const nextOrderBy: 'asc' | 'desc' =
        isActive && sortDirection === 'desc' ? 'asc' : 'desc';
      // Canonicalización: default (registerInitDate + desc) viaja sin sortField
      const isDefault =
        sortField === DEFAULT_SORT_FIELD && nextOrderBy === 'desc';
      const dataCursor = {
        sortField: isDefault ? undefined : sortField,
        orderBy: nextOrderBy,
        cursor: '',
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setCurrentServerPage(1);
      onReFetchData?.();
    },
    [
      activeSortField,
      sortDirection,
      dispatch,
      tableId,
      setCurrentServerPage,
      onReFetchData,
    ]
  );

  const sortableHeader = useCallback(
    (columnId: keyof typeof SORTABLE_COLUMNS) => () => (
      <SortableHeader
        label={
          translatedColumnNames[columnId as keyof typeof translatedColumnNames]
            ?.label ?? columnId
        }
        active={activeSortColumnId === columnId}
        direction={sortDirection}
        onToggle={() => onSortColumn(SORTABLE_COLUMNS[columnId])}
      />
    ),
    [translatedColumnNames, activeSortColumnId, sortDirection, onSortColumn]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'select',
          header: ({ table }) => (
            <InputCheckContainer>
              <CheckBox
                name={'select'}
                {...{
                  checked: table.getIsAllRowsSelected(),
                  indeterminate: table.getIsSomeRowsSelected(),
                  onChange: table.getToggleAllRowsSelectedHandler(),
                }}
              />
            </InputCheckContainer>
          ),
          cell: ({ row }) => (
            <InputCheckContainer
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <CheckBoxStyled
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: row.getToggleSelectedHandler(),
                }}
              />
            </InputCheckContainer>
          ),
        }),
        // Banderitas (spec 2026-10-22 § 8.2): justo detrás de la selección y
        // solo si la pantalla la pide.
        ...(showFlagColumn
          ? [
              createFlagColumn<dataTable>(
                getFlagOrderId),
            ]
          : []),
        columnHelper.accessor(getColumnId(translatedColumnNames.state.id), {
          header: sortableHeader('state'),
          cell: (info) => (
            <SpecialTag
              sizeTag={sizeTag}
              variant={info.getValue() as variant}
            />
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.roid.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.roid.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {formatRoidForDisplay(info.getValue())}
            </TextCellStyled>
          ),
          filterFn: 'roidFilter' as FilterFnOption<Order>, //Indicate the filter function to be used for this column
        }),
        // V4 FR-1.6 — columna "Nom del recorregut" opcional para SR.
        ...(showRouteNameColumn
          ? [
              columnHelper.accessor('name' as any, {
                id: 'name',
                header: () => (
                  <SortableHeader
                    label={t('pages:surveillance.name', 'Nom del recorregut')}
                    active={activeSortColumnId === 'name'}
                    direction={sortDirection}
                    onToggle={() => onSortColumn(SORTABLE_COLUMNS.name)}
                  />
                ),
                cell: (info) => (
                  <TextCellStyled size={'S'}>
                    {(info.getValue() as string) || '—'}
                  </TextCellStyled>
                ),
              }),
            ]
          : []),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.registerInitDate.id),
          {
            header: sortableHeader('registerInitDate'),
            cell: (info) => (
              <TextCellStyled size={'S'}>
                {format(
                  new Date(info.getValue() as string),
                  'dd/MM/yyyy - HH:mm'
                )}
              </TextCellStyled>
            ),
            filterFn: 'dateFilter' as FilterFnOption<dataTable>,
          }
        ),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.derivedEndDate.id),
          {
            // Solo RV: header ordenable (sortField backend `registerEndDate`).
            // En el resto de tablas derivedEndDate NO está en la whitelist →
            // header plano como siempre.
            header: isSurveillanceTable
              ? sortableHeader('derivedEndDate')
              : () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {translatedColumnNames.derivedEndDate.label}
                  </TextHeader>
                ),
            cell: (info) => {
              const v = info.getValue() as string | null | undefined;
              return (
                <TextCellStyled size={'S'}>
                  {v ? format(new Date(v), 'dd/MM/yyyy - HH:mm') : '—'}
                </TextCellStyled>
              );
            },
            filterFn: 'dateFilter' as FilterFnOption<dataTable>,
          }
        ),

        columnHelper.accessor(getColumnId(translatedColumnNames.capitol.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.capitol.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {info.getValue() as string}
            </TextCellStyled>
          ),
        }),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.subCapitol.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.subCapitol.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCellStyled size={'S'}>
                {info.getValue() as string}
              </TextCellStyled>
            ),
          }
        ),

        columnHelper.accessor('predefinedOrder' as any, {
          id: 'predefinedOrder',
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.predefinedOrder?.label ?? 'Predefinits'}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {(info.getValue() as string) ?? ''}
            </TextCellStyled>
          ),
        }),

        columnHelper.accessor(
          getColumnId(translatedColumnNames.concession.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.concession.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCellStyled size={'S'}>
                {info.getValue() as string}
              </TextCellStyled>
            ),
          }
        ),
        columnHelper.accessor(getColumnId(translatedColumnNames.team.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.team.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {(info.getValue() as string) || ''}
            </TextCellStyled>
          ),
        }),
        // Columna "Creado por" (solo RV, 2026-07-17): userId de la Order gemela
        // resuelto a nombre por el adapter (row.creatorName). Mismo precedente
        // que showRouteNameColumn. Header plano — el creador NO está en la
        // whitelist de ordenación del backend.
        ...(showCreatorColumn
          ? [
              columnHelper.accessor('creatorName' as any, {
                id: 'creator',
                header: () => (
                  <TextHeader size={'XS'} colorText={'greyDark'}>
                    {t('common:labels.creator')}
                  </TextHeader>
                ),
                cell: (info) => (
                  <TextCellStyled size={'S'}>
                    {(info.getValue() as string) || '—'}
                  </TextCellStyled>
                ),
              }),
            ]
          : []),
        columnHelper.accessor(getColumnId(translatedColumnNames.road.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.road.label}
            </TextHeader>
          ),
          // 🔴 Una línea por carretera, igual que la Vista general del
          // recorrido: los dos listados sirven el MISMO texto (sale de
          // `useSurveillanceRoutesListing`, con saltos de línea) y aquí se
          // aplastaba en una sola línea. Ver `PkCell` para el porqué.
          cell: (info) => (
            <MultiLineCell size={'S'}>
              {info.getValue() as string}
            </MultiLineCell>
          ),
        }),
        columnHelper.accessor('track' as any, {
          id: 'track',
          header: sortableHeader('track'),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {info.getValue() as string}
            </TextCellStyled>
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.direction.id), {
          header: sortableHeader('direction'),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {info.getValue() as string}
            </TextCellStyled>
          ),
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.pkInit.id), {
          header: sortableHeader('pkInit'),
          cell: (info) => <PkCell value={info.getValue() as string} />,
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.pkEnd.id), {
          header: sortableHeader('pkEnd'),
          cell: (info) => <PkCell value={info.getValue() as string} />,
        }),
        columnHelper.accessor(getColumnId(translatedColumnNames.user.id), {
          header: () => (
            <TextHeader size={'XS'} colorText={'greyDark'}>
              {translatedColumnNames.user.label}
            </TextHeader>
          ),
          cell: (info) => (
            <TextCellStyled size={'S'}>
              {(info.getValue() as string) || ''}
            </TextCellStyled>
          ),
        }),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.description.id),
          {
            header: () => (
              <TextHeader size={'XS'} colorText={'greyDark'}>
                {translatedColumnNames.description.label}
              </TextHeader>
            ),
            cell: (info) => (
              <TextCellStyled size={'S'}>
                {info.getValue() as string}
              </TextCellStyled>
            ),
          }
        ),
        columnHelper.accessor(
          getColumnId(translatedColumnNames.creationDate.id),
          {
            header: sortableHeader('creationDate'),
            cell: (info) => (
              <TextCellStyled size={'S'}>
                {format(
                  new Date(info.getValue() as string),
                  'dd/MM/yyyy - HH:mm'
                )}
              </TextCellStyled>
            ),
          }
        ),
      ].filter((col: any) => {
        // V4 FR-1.6 — caller puede ocultar columnas por id (e.g. surveillance
        // route excluye Capítulo/Subcapítulo/Tipo vial/Sentido/Predefinidos/
        // Fecha sistema porque no aplican al dominio).
        if (!hiddenColumnIds?.length) return true;
        const colId = (col as any).id ?? (col as any).accessorKey;
        return !hiddenColumnIds.includes(colId);
      }),
    [
      isPortraitTablet,
      sizeTag,
      translatedColumnNames,
      hiddenColumnIds,
      showRouteNameColumn,
      showCreatorColumn,
      isSurveillanceTable,
      sortableHeader,
      showFlagColumn,
      getFlagOrderId,
      t,
    ]
  );

  // Tarea #11 — Sincronizar TanStack pageSize con `takePagination` (server).
  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize: takePagination,
    }),
    [pageIndex, takePagination]
  );

  const filterFns = {
    roidFilter: roidFilterFunction,
    dateFilter: dateFilterFunction,
  };

  const table = useReactTable({
    getRowId: (row: any) => row.id,
    data: dataAll,
    columns,
    state: {
      rowSelection,
      columnFilters,
      pagination,
      columnVisibility,
    },
    filterFns,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true, //enable row selection for all rows
    enableMultiRowSelection: true,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Flag to skip sync after restore (prevents infinite loop)
  const isRestoringRef = useRef(false);

  // Update rowState when selection changes (rowSelection uses MongoDB IDs via getRowId)
  // Only needed in uncontrolled mode — controlled mode manages state in the parent
  useEffect(() => {
    if (isControlled) return;
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    const selectedIds = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    setRowState?.(selectedIds);
  }, [rowSelection, setRowState, isControlled]);

  // Restore persistent selections when page data changes
  // Only needed in uncontrolled mode — controlled mode computes rowSelection in the parent
  useEffect(() => {
    if (isControlled) return;
    const newSelection: Record<string, boolean> = {};
    if (selectAllMode) {
      dataAll.forEach((row: any) => {
        if (row.id && !excludedIdsRef.current?.has(row.id)) {
          newSelection[row.id] = true;
        }
      });
    } else if (rowStateRef.current && rowStateRef.current.length > 0) {
      const persistedSet = new Set(rowStateRef.current);
      dataAll.forEach((row: any) => {
        if (row.id && persistedSet.has(row.id)) {
          newSelection[row.id] = true;
        }
      });
    }
    isRestoringRef.current = true;
    setInternalRowSelection(newSelection);
  }, [dataAll, selectAllMode, isControlled]);

  const capitolOptions = data?.filtersDataOptions?.capitols.map((capitol) => ({
    label: capitol.name,
    value: capitol.id,
    subCapitolsIDs: capitol.subCapitolsIDs,
  }));

  const concessionOptions = data?.filtersDataOptions?.concessions.map(
    (concession) => ({
      label: concession.name,
      value: concession.id,
    })
  );

  const teamOptions = useCallback(() => {
    return data?.filtersDataOptions?.teams?.map(
      (team: { name: string; id: string }) => ({
        label: team.name,
        value: team.id,
      })
    );
  }, [data?.filtersDataOptions?.teams]);

  // FR-2.3 (RV): opciones de Carretera limitadas a la CONCESIÓN seleccionada
  // (o la única, auto-seleccionada) — mismo patrón que el listado (P1) y las
  // tablas de comunicaciones. Sin concesión → todas.
  const roadOptions = useMemo(() => {
    const allRoads = (data?.filtersDataOptions?.roads ?? []).map((road) => ({
      label: road.name,
      value: road.id,
    }));
    const concessionId =
      selectorData?.concessionId ?? selectorCursor?.concessionId;
    if (!concessionId) return allRoads;
    const concession = data?.filtersDataOptions?.concessions?.find(
      (c) => c.id === concessionId
    );
    if (!concession?.roadIDs?.length) return allRoads;
    return allRoads.filter((road) => concession.roadIDs?.includes(road.value));
  }, [
    data?.filtersDataOptions?.roads,
    data?.filtersDataOptions?.concessions,
    selectorData?.concessionId,
    selectorCursor?.concessionId,
  ]);

  // Auto-select the only concession if there's only one available
  useEffect(() => {
    if (
      concessionOptions &&
      concessionOptions.length === 1 &&
      !selectorCursor.concessionId
    ) {
      const onlyConcession = concessionOptions[0];
      setSelectorCursor((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
      setSelectorData((prev) => ({
        ...prev,
        concessionId: onlyConcession.value,
      }));
    }
  }, [concessionOptions, selectorCursor.concessionId]);

  const classificationOptions = useMemo(
    () =>
      data?.filtersDataOptions?.classifications
        ?.slice()
        ?.sort(
          (a, b) => (a.classificationCode ?? 0) - (b.classificationCode ?? 0)
        )
        ?.map((classification) => ({
          label: `${classification.classificationCode} - ${classification.name}`,
          value: classification.id,
        })),
    [data?.filtersDataOptions?.classifications]
  );

  const trackOptions = useMemo(() => getTrackOptions(t), [t]);

  const predefinedOrderOptions =
    data?.filtersDataOptions?.predefinedOrders?.map(
      (predefined: { id: string; name: string }) => ({
        label: predefined.name,
        value: predefined.id,
      })
    );

  //I select the column and we pass the filter with the input values
  const onChangeInputExpedient = useCallback<onChangeInputExpedientTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData.indexType ?? selectorCursor.indexType,
        indexExpedition: value,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexExpedition = value;
        return newData;
      });
      setExpedientDefault(value);
    },
    [
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData.indexType,
      selectorData.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeInputIndex = useCallback<onChangeInputIndexTypes>(
    async (value) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: value,
        indexExpedition:
          selectorData.indexExpedition ?? selectorCursor.indexExpedition,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData.classification ?? selectorCursor.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = { ...prevData };
        newData.indexType = value;
        return newData;
      });
      setIndexDefault(value);
    },
    [
      selectorCursor,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.states,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData.indexExpedition,
      selectorData.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangePKInit = useCallback<onChangeInputPKTypes>((km, meter) => {
    setSelectorData((prevData) => ({
      ...prevData,
      pkInitKm: km,
      pkInitMeter: meter,
    }));
  }, []);

  const onChangePKEnd = useCallback<onChangeInputPKTypes>((km, meter) => {
    setSelectorData((prevData) => ({
      ...prevData,
      pkEndKm: km,
      pkEndMeter: meter,
    }));
  }, []);

  /**
   * *Function for filtering date ranges
   * @param range is a range of dates ({from, to})
   */

  const onChangeInput = useCallback<onChangeInputType>(
    (range) => {
      if (range.to) {
        range.to.setHours(23);
        range.to.setMinutes(59);
        range.to.setSeconds(59);
        range.to.setMilliseconds(999);
      }
      const fromDateIso = formatISO(range.from as Date);
      const toDateIso = formatISO(range.to as Date);
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: selectorData.subCapitolId ?? selectorCursor.subCapitolId,
        capitolId: selectorData.capitolId ?? selectorCursor.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: fromDateIso,
        toDate: toDateIso,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.fromDate = fromDateIso;
        newData.toDate = toDateIso;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedConcession = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId: option.value,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.concessionId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedTeam = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextTeamIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: nextTeamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, teamIds: nextTeamIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.capitolId,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.teamIds,
      dispatch,
      tableId,
    ]
  );

  const onChangeSelectedCapitolGroup = useCallback(
    async (option: option) => {
      const {
        data: { readSubCapitols },
      } = await client.query({
        query: GET_SUB_CAPITOLS_BY_IDS,
        variables: {
          ids: option.subCapitolsIDs,
        },
      });
      const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setSubCapitolOptions(
        sortedSubCapitols?.map((road: { id: string; name: string }) => ({
          value: road.id,
          label: road.name,
        }))
      );
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: undefined,
        capitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.capitolId = option.value;
        newData.subCapitolId = undefined;
        return newData;
      });

      inputListSubcapitolRef.current?.clearSelected();
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedSubcapitolGroup = useCallback(
    (option: option) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId: option.value,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => {
        const newData = prevData;
        newData.subCapitolId = option.value;
        return newData;
      });
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
    ]
  );

  const onChangeSelectedStates = useCallback(
    (values: string[]) => {
      // Canonicalize: sort stable so Apollo cache partitioning by `keyArgs: [..., 'states']`
      // produces the same key for the same selection regardless of click order.
      const sorted = [...values].sort();
      const nextStates = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        orderBy: 'desc',
        states: nextStates,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, states: nextStates }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dispatch,
      tableId,
    ]
  );

  // Multi-select creador. Patrón canónico replicado de AccidentsTable /
  // InputRadioTagTable.onChangeSelectedUserIds.
  const onChangeSelectedUserIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextUserIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: selectorCursor?.cursor,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: nextUserIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, userIds: nextUserIds }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.teamIds,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.includeInactive,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.track,
      dispatch,
      tableId,
    ]
  );

  // Multi-select Operarios (solo RV, gated por `showOperatorsFilter`): filtra
  // por RV.userIDs (operarios del recorrido) vía `operatorIds` del backend.
  // Mismo patrón canónico que onChangeSelectedUserIds (orden estable para no
  // fragmentar la cache Apollo; el cursor Redux hace MERGE del resto).
  const onChangeSelectedOperatorIds = useCallback(
    (values: string[]) => {
      const sorted = [...values].sort();
      const nextOperatorIds = sorted.length > 0 ? sorted : undefined;
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: nextOperatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({
        ...prevData,
        operatorIds: nextOperatorIds,
      }));
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.subCapitolId,
      selectorData?.concessionId,
      selectorData?.capitolId,
      selectorData?.teamIds,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.predefinedOrderId,
      selectorData?.states,
      selectorData?.userIds,
      selectorData?.includeInactive,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      selectorData?.track,
      dispatch,
      tableId,
    ]
  );

  const onChangeIncludeInactive = useCallback(
    (next: boolean) => {
      const dataCursor = {
        take: selectorCursor.take,
        cursor: '',
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        orderBy: 'desc',
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive: next,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        track: selectorData?.track ?? selectorCursor?.track,
      };
      dispatch(setCursor(tableId as string, dataCursor));
      setSelectorData((prevData) => ({ ...prevData, includeInactive: next }));
    },
    [selectorCursor, selectorData, dispatch, tableId]
  );

  // «Solo marcadas» (spec 2026-10-22 § 8.3). Solo guarda en el cursor —el
  // reducer hace merge, así que basta con su clave— y la búsqueda la lanza
  // Buscar. Desmarcado es `undefined`, nunca `false`: así la caché de Apollo no
  // se parte en dos claves para «sin filtro». `selectorData` no lleva la clave
  // (arranca con un objeto propio, no con el cursor), así que el selector de
  // tamaño de página la toma del reflejo del cursor, que ya está al día.
  const onChangeOnlyFlagged = useCallback(
    (next: boolean) => {
      dispatch(
        setCursor(tableId as string, { onlyFlagged: next ? true : undefined })
      );
    },
    [dispatch, tableId]
  );

  const userOptions = useMemo(() => {
    const base = filterAndSortMultimediaUsers(
      data?.filtersDataOptions?.users ?? [],
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.userIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.userIds,
  ]);

  // Opciones del dropdown Operarios (solo RV, gated por showOperatorsFilter).
  // Filtra por userType 'operator' (operario de campo sin login) — mismo
  // patrón que ValidationsTable. Merge missing ids para que chips de
  // operarios inactivos previamente marcados sigan renderizando.
  const operatorOptions = useMemo(() => {
    if (!showOperatorsFilter) return [];
    const base = filterAndSortOperators(
      data?.filtersDataOptions?.users ?? [],
      selectorCursor?.includeInactive ?? false
    );
    const selectedIds = selectorCursor?.operatorIds ?? [];
    const missingIds = selectedIds.filter(
      (id: string) => !base.find((o) => o.value === id)
    );
    if (missingIds.length === 0) return base;
    const allUsers = data?.filtersDataOptions?.users ?? [];
    const missingOptions = missingIds
      .map((id: string) => allUsers.find((u: { id: string }) => u.id === id))
      .filter(
        (
          u: unknown
        ): u is {
          id?: string;
          name?: string;
          surName?: string;
          isActive?: boolean;
        } => Boolean(u)
      )
      .map((u) => ({
        label:
          u?.isActive === false
            ? `${u?.name ?? ''} ${u?.surName ?? ''} (inactivo)`.trim()
            : `${u?.name ?? ''} ${u?.surName ?? ''}`.trim(),
        value: u?.id ?? '',
      }));
    return [...base, ...missingOptions];
  }, [
    showOperatorsFilter,
    data?.filtersDataOptions?.users,
    selectorCursor?.includeInactive,
    selectorCursor?.operatorIds,
  ]);

  const onChangeSelectedClassification = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.classification = value.value;
      return newData;
    });
  };

  const onChangeSelectedTrack = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.track = value.value;
      return newData;
    });
  };

  const onChangeSelectedPredefinedOrder = (value: option) => {
    setSelectorData((prevData) => {
      const newData = { ...prevData };
      newData.predefinedOrderId = value.value;
      return newData;
    });
  };

  const onClearValue = useCallback(() => {
    console.log(
      '[CheckBoxTagTable][onClearValue] BEFORE — selectorCursor:',
      selectorCursor,
      '| selectorData:',
      selectorData,
      '| globalCursor:',
      globalCursor
    );
    inputListCapitolRef.current?.clearSelected();
    inputListSubcapitolRef.current?.clearSelected();
    inputListPredefinedRef.current?.clearSelected();
    inputDateRef.current?.clearSelected();
    inputExpedientRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputIndexRef.current?.clearSelected();
    inputStateRef.current?.clearSelected();
    inputUserRef.current?.clearSelected();
    inputOperatorsRef.current?.clearSelected();
    inputListTeamRef.current?.clearSelected();
    inputListClassificationRef.current?.clearSelected();
    inputListTrackRef.current?.clearSelected();
    inputListRoadRef.current?.clearSelected();
    inputPKInitRef.current?.clearSelected();
    inputPKEndRef.current?.clearSelected();
    setColumnFilters([]);

    // Check if there's only one concession
    const hasOnlyOneConcession =
      concessionOptions && concessionOptions.length === 1;
    const onlyConcessionId = hasOnlyOneConcession
      ? concessionOptions[0].value
      : undefined;

    // If there's only one concession, keep it selected
    // Otherwise, clear it
    if (!hasOnlyOneConcession) {
      inputListConRef.current?.clearSelected();
    }

    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      orderBy: 'desc' as const,
      sortField: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
      routeName: undefined,
      roadId: undefined,
      // Explícito: el reducer hace merge y omitirlo conservaría el filtro.
      onlyFlagged: undefined,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setSelectorCursor((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      orderBy: 'desc' as const,
      sortField: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
      routeName: undefined,
      roadId: undefined,
      onlyFlagged: undefined,
    }));
    setExpedientDefault(undefined);
    setIndexDefault(undefined);
    setSubCapitolOptions(initialSubCapitolsOptions);
    setSelectorData((prevData) => ({
      ...prevData,
      indexType: undefined,
      indexExpedition: undefined,
      subCapitolId: undefined,
      capitolId: undefined,
      concessionId: hasOnlyOneConcession ? onlyConcessionId : undefined,
      teamIds: undefined,
      fromDate: undefined,
      toDate: undefined,
      classification: undefined,
      track: undefined,
      state: undefined,
      states: undefined,
      userIds: undefined,
      operatorIds: undefined,
      includeInactive: undefined,
      pkInitKm: undefined,
      pkInitMeter: undefined,
      pkEndKm: undefined,
      pkEndMeter: undefined,
      description: undefined,
      predefinedOrderId: undefined,
      routeName: undefined,
      roadId: undefined,
    }));
    setSubCapitolOptions(initialSubCapitolsOptions);
    setCurrentServerPage(1);
    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al cambiar/limpiar el filtro se reinicia la selección (no en paginación)
    // Tarea #11 fix: `takePagination` debe estar en deps para evitar stale closure.
  }, [
    concessionOptions,
    onChangeInputExpedient,
    onChangeInputIndex,
    takePagination,
  ]);

  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      if (isControlled) {
        // In controlled mode, toggle via TanStack's row handler
        row.toggleSelected();
        return;
      }
      const val: boolean = row.getIsSelected();
      const newRowState = [...(rowState || [])];
      if (!val) {
        newRowState.push(row.original.id);
        setRowState?.(newRowState);
      } else {
        const index = newRowState.indexOf(row.original.id);
        if (index !== -1) {
          newRowState.splice(index, 1);
        }
        setRowState?.(newRowState);
      }
    },
    [rowState, isControlled]
  );

  const fetchOrderID = useCallback(
    async (id: string, take = takePagination) => {
      const dataSelector = {
        take,
        cursor: id,
        indexType: selectorData?.indexType ?? selectorCursor?.indexType,
        indexExpedition:
          selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
        concessionId:
          selectorData?.concessionId ?? selectorCursor?.concessionId,
        teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
        fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
        toDate: selectorData?.toDate ?? selectorCursor?.toDate,
        sortField: selectorCursor?.sortField,
        orderBy: selectorCursor?.orderBy ?? 'desc',
        classification:
          selectorData?.classification ?? selectorCursor?.classification,
        track: selectorData?.track ?? selectorCursor?.track,
        states: selectorData?.states ?? selectorCursor?.states,
        userIds: selectorData?.userIds ?? selectorCursor?.userIds,
        operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
        includeInactive:
          selectorData?.includeInactive ?? selectorCursor?.includeInactive,
        pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
        pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
        pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
        pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
        predefinedOrderId:
          selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
        capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
        subCapitolId:
          selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      };

      if (take > 0) {
        const lastGenericOrder =
          dataAll[dataAll.length - 1].genericOrCampaignOrderID;
        if (lastGenericOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      } else {
        const firstGenericOrder = dataAll[0].genericOrCampaignOrderID;

        if (firstGenericOrder) {
          dispatch(setCursor(tableId as string, dataSelector));
        }
      }

      return dataSelector;
    },
    [
      selectorCursor,
      selectorData?.indexType,
      selectorData?.indexExpedition,
      selectorData?.concessionId,
      selectorData?.fromDate,
      selectorData?.toDate,
      selectorData?.classification,
      selectorData?.capitolId,
      selectorData?.subCapitolId,
      selectorData?.states,
      selectorData?.pkInitKm,
      selectorData?.pkEndKm,
      dataAll,
      takePagination,
    ]
  );

  const nextPagination = async () => {
    if (!isControlled) setRowSelection({});
    const lastItemId = dataAll[dataAll.length - 1]
      ?.genericOrCampaignOrderID as string;
    // Clamp defensivo contra bug off-by-one (backend/cache hasNextPage true en última real).
    const pageCount = Math.max(
      1,
      Math.ceil((data?.totalCount ?? 0) / takePagination)
    );
    console.log(
      `%c[TABLE] nextPage: currentPage=${currentServerPage} cursor=${lastItemId?.slice(0, 8)}… hasNextPage=${data?.pageInfo.hasNextPage} dataAll=${dataAll.length}`,
      'color: #00cc00; font-weight: bold'
    );
    if (
      dataAll.length >= 1 &&
      data?.pageInfo.hasNextPage &&
      currentServerPage < pageCount
    ) {
      setCurrentServerPage((prev) => Math.min(prev + 1, pageCount));
      await fetchOrderID(lastItemId);
      onReFetchPagination();
    }
  };

  const backPagination = async () => {
    if (!isControlled) setRowSelection({});
    const firstItemId = dataAll[0]?.genericOrCampaignOrderID as string;
    console.log(
      `%c[TABLE] backPage: currentPage=${currentServerPage} cursor=${firstItemId?.slice(0, 8)}… hasPreviousPage=${data?.pageInfo.hasPreviousPage} dataAll=${dataAll.length}`,
      'color: #00cc00; font-weight: bold'
    );
    if (dataAll.length >= 1 && data?.pageInfo.hasPreviousPage) {
      setCurrentServerPage((prev) => Math.max(1, prev - 1));
      await fetchOrderID(firstItemId, -takePagination);
      onReFetchPagination();
    }
  };

  // Columnas que ofrece el desplegable «Columnas»: todas menos la de selección
  // (la primera) y las que no se pueden ocultar (la bandera, § 8.2). Sin
  // bandera queda igual que el `.slice(1)` de siempre.
  const hideableColumns = table
    .getAllColumns()
    .slice(1)
    .filter((column) => column.getCanHide());

  const hiddenColumns: number = Object.values(
    columnVisibility as object
  ).reduce((count, currentValue) => (!currentValue ? count + 1 : count), 0);

  // commonColumns definition removed

  // commonLabels definition removed

  const handleSearch = async () => {
    const dataCursor = {
      take: takePagination,
      cursor: '',
      indexType: selectorData?.indexType ?? selectorCursor?.indexType,
      indexExpedition:
        selectorData?.indexExpedition ?? selectorCursor?.indexExpedition,
      subCapitolId: selectorData?.subCapitolId ?? selectorCursor?.subCapitolId,
      capitolId: selectorData?.capitolId ?? selectorCursor?.capitolId,
      concessionId: selectorData?.concessionId ?? selectorCursor?.concessionId,
      teamIds: selectorData?.teamIds ?? selectorCursor?.teamIds,
      fromDate: selectorData?.fromDate ?? selectorCursor?.fromDate,
      toDate: selectorData?.toDate ?? selectorCursor?.toDate,
      classification:
        selectorData?.classification ?? selectorCursor?.classification,
      track: selectorData?.track ?? selectorCursor?.track,
      sortField: selectorCursor?.sortField,
      orderBy: selectorCursor?.orderBy ?? 'desc',
      states: selectorData?.states ?? selectorCursor?.states,
      userIds: selectorData?.userIds ?? selectorCursor?.userIds,
      operatorIds: selectorData?.operatorIds ?? selectorCursor?.operatorIds,
      includeInactive:
        selectorData?.includeInactive ?? selectorCursor?.includeInactive,
      pkInitKm: selectorData?.pkInitKm ?? selectorCursor?.pkInitKm,
      pkInitMeter: selectorData?.pkInitMeter ?? selectorCursor?.pkInitMeter,
      pkEndKm: selectorData?.pkEndKm ?? selectorCursor?.pkEndKm,
      pkEndMeter: selectorData?.pkEndMeter ?? selectorCursor?.pkEndMeter,
      description:
        (selectorData?.description ?? selectorCursor?.description) || undefined,
      predefinedOrderId:
        selectorData?.predefinedOrderId ?? selectorCursor?.predefinedOrderId,
      routeName:
        (selectorData?.routeName ?? selectorCursor?.routeName) || undefined,
      roadId: selectorData?.roadId ?? selectorCursor?.roadId,
    };
    dispatch(setCursor(tableId as string, dataCursor));
    setCurrentServerPage(1);
    onReFetchData?.();
    onClearSelection?.(); // Bug #2: al buscar con filtro nuevo se reinicia la selección
  };

  const filterContainerRef = useRef<HTMLDivElement>(null);
  useFilterKeyboardShortcuts(
    filterContainerRef,
    handleSearch,
    onClearValue,
    isLoadingData as boolean
  );

  return (
    <ContainerStyled>
      {isLoadingData && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <FiltersWithLoadingStyled isLoading={isLoadingData}>
        <SubContainerHead>
          <Title component={'h2'} variant={'semiBold'} size={'M'}>
            {titleText}
          </Title>
          {isLanscapeTablet && (
            <ShowFiltersButton isBlack={showFilters} onClick={handleClick}>
              <ArrowBottomStyled isRotate={showFilters} />
            </ShowFiltersButton>
          )}
        </SubContainerHead>
        {showFilters && (
          <SubContainerFiltersStyled ref={filterContainerRef}>
            {!isFilterHidden('expedient') && (
              <InputExpedient
                label={t('headers.expedient')}
                disabled={isLoadingData}
                expedient={expedientDefault}
                onChangeInputExpedient={onChangeInputExpedient}
                ref={inputExpedientRef}
              />
            )}

            {!isFilterHidden('index') && (
              <InputIndex
                label={t('headers.index')}
                disabled={isLoadingData}
                index={indexDefault}
                onChangeInputIndex={onChangeInputIndex}
                ref={inputIndexRef}
              />
            )}

            {/* FR-2.3 (RV): filtro Nombre del recorrido — gate showRouteNameColumn
                (solo lo activa el histórico RV). Label corto (P1). */}
            {showRouteNameColumn && (
              <InputText
                label={t('common:labels.name')}
                typeInput="normal"
                inputSize="S"
                placeholder={t('common:placeholders.writeHere')}
                disabled={isLoadingData}
                value={
                  selectorData?.routeName ?? selectorCursor?.routeName ?? ''
                }
                onChange={(e) =>
                  setSelectorData((prev) => ({
                    ...prev,
                    routeName: e.target.value,
                  }))
                }
              />
            )}

            {/* FR-2.3 (RV): filtro Carretera, opciones por concesión (roadOptions). */}
            {showRouteNameColumn && roadOptions.length > 0 && (
              <SearchableDropdownStyled
                ref={inputListRoadRef}
                inputSize="L"
                typeDropDown="Default"
                labelText={t('tables:headers.road')}
                placeholder={t('common:placeholders.select')}
                options={roadOptions as Options}
                defaultValue={
                  selectorData?.roadId ?? selectorCursor?.roadId ?? ''
                }
                onChangeSelected={
                  ((option: option) =>
                    setSelectorData((prev) => ({
                      ...prev,
                      roadId: option?.value || undefined,
                    }))) as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}

            {!isFilterHidden('pkInit') && (
              <InputPK
                label={t('tables:headers.pkInit')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKInit}
                ref={inputPKInitRef}
              />
            )}
            {!isFilterHidden('pkEnd') && (
              <InputPK
                label={t('tables:headers.pkEnd')}
                disabled={isLoadingData}
                onChangeInputPK={onChangePKEnd}
                ref={inputPKEndRef}
              />
            )}
            {/* Filtro por descripción: texto libre sobre Order.description.
                Busca por subcadena, sin distinguir acentos ni mayúsculas, y
                exige todas las palabras en cualquier orden (el backend filtra
                contra la copia normalizada `descriptionSearch`, indexada). */}
            <InputText
              label={t('tables:headers.description')}
              typeInput="normal"
              inputSize="S"
              placeholder={t('common:placeholders.writeHere')}
              disabled={isLoadingData}
              value={
                selectorData?.description ?? selectorCursor?.description ?? ''
              }
              onChange={(e) =>
                setSelectorData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            {!isFilterHidden('date') && (
              <InputCalendarStyled
                ref={inputDateRef}
                label={t('tables:filters.dateRangeFilter')}
                name={'data'}
                onChangeInput={onChangeInput}
                dateRange={
                  selectorCursor?.fromDate && selectorCursor?.toDate
                    ? {
                        from: parseISO(selectorCursor?.fromDate),
                        to: parseISO(selectorCursor?.toDate),
                      }
                    : undefined
                }
                disabled={isLoadingData}
              />
            )}
            {/* Solo mostrar selector de concesión si hay más de una */}
            {!isFilterHidden('concession') &&
              concessionOptions &&
              concessionOptions.length > 1 && (
                <DropDownInput
                  inputSize="XS"
                  ref={inputListConRef}
                  labelText={t('tables:headers.concession')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Secondary'}
                  defaultValue={selectorCursor.concessionId ?? ''}
                  options={concessionOptions as Options}
                  onChangeSelected={onChangeSelectedConcession as onChangeValue}
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('team') &&
              teamOptions() &&
              (teamOptions()?.length ?? 0) > 0 && (
                <MultiSelectDropdown
                  inputSize="M"
                  ref={inputListTeamRef}
                  labelText={t('forms:workOrder.team')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Secondary'}
                  selectedValues={selectorCursor?.teamIds ?? []}
                  options={teamOptions() as Options}
                  onChangeSelected={onChangeSelectedTeam}
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('capitol') && showCapitolFilter && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListCapitolRef}
                labelText={'Capitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor.capitolId ?? ''}
                options={capitolOptions as Options}
                onChangeSelected={onChangeSelectedCapitolGroup as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}

            {!isFilterHidden('subCapitol') && showSubCapitolFilter && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListSubcapitolRef}
                labelText={'Subcapitol'}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                options={subCapitolOptions as Options}
                defaultValue={selectorCursor.subCapitolId ?? ''}
                onChangeSelected={
                  onChangeSelectedSubcapitolGroup as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}

            {!isFilterHidden('predefinedOrder') &&
              showPredefinedFilter &&
              predefinedOrderOptions &&
              predefinedOrderOptions.length > 0 && (
                <SearchableDropdownStyled
                  inputSize="L"
                  ref={inputListPredefinedRef}
                  labelText={t('forms:workOrder.predefined')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  defaultValue={selectorCursor?.predefinedOrderId ?? ''}
                  options={predefinedOrderOptions as Options}
                  onChangeSelected={
                    onChangeSelectedPredefinedOrder as onChangeValue
                  }
                  isDisabled={isLoadingData}
                />
              )}
            {!isFilterHidden('classification') && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListClassificationRef}
                labelText={t('tables:headers.classification')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.classification ?? ''}
                options={classificationOptions as Options}
                onChangeSelected={
                  onChangeSelectedClassification as onChangeValue
                }
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('track') && (
              <SearchableDropdownStyled
                inputSize="L"
                ref={inputListTrackRef}
                labelText={t('forms:workOrder.roadType')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                defaultValue={selectorCursor?.track ?? ''}
                options={trackOptions as Options}
                onChangeSelected={onChangeSelectedTrack as onChangeValue}
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('state') && (
              <MultiSelectDropdown
                inputSize="S"
                ref={inputStateRef}
                labelText={t('tables:headers.status')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Secondary'}
                selectedValues={selectorCursor?.states ?? []}
                options={
                  roleActive
                    ? getStateOptionsByRoleAndEntityType(
                        t,
                        roleActive,
                        TABLE_ENTITY_TYPES[tableId as string],
                        listingFiltersByRole(tableId)
                      )
                    : []
                }
                onChangeSelected={onChangeSelectedStates}
                isDisabled={isLoadingData}
              />
            )}
            {!isFilterHidden('creator') &&
              userOptions &&
              userOptions.length > 0 && (
                <MultiSelectDropdownStyled
                  inputSize="L"
                  ref={inputUserRef}
                  labelText={t('common:labels.creator')}
                  placeholder={t('common:placeholders.select')}
                  typeDropDown={'Default'}
                  searchable
                  selectedValues={selectorCursor?.userIds ?? []}
                  options={userOptions}
                  onChangeSelected={onChangeSelectedUserIds}
                  isDisabled={isLoadingData}
                />
              )}
            {/* Filtro Operarios (solo RV): filtra por RV.userIDs — mismo
                patrón multi-select que Creador/Operarios de Actuaciones. */}
            {showOperatorsFilter && operatorOptions.length > 0 && (
              <MultiSelectDropdownStyled
                inputSize="L"
                ref={inputOperatorsRef}
                labelText={t('forms:workOrder.operators')}
                placeholder={t('common:placeholders.select')}
                typeDropDown={'Default'}
                searchable
                selectedValues={selectorCursor?.operatorIds ?? []}
                options={operatorOptions}
                onChangeSelected={onChangeSelectedOperatorIds}
                isDisabled={isLoadingData}
              />
            )}
            <ContainerButtonStyled>
              <ButtonIconBackground
                variant={'delete'}
                onClick={onClearValue}
                disabled={isLoadingData}
                title={`${t('tables:filters.clearFilters')} (Shift+Enter)`}
              />
              <DropDownVisibilityColumn
                columnNames={Object.fromEntries(
                  hideableColumns.map((c) => [
                    c.id,
                    translatedColumnNames[
                      c.id as keyof typeof translatedColumnNames
                    ]?.label ||
                      (c.id === 'creator' ? t('common:labels.creator') : c.id),
                  ])
                )}
                columns={hideableColumns}
                isDisabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'restart'}
                onClick={() => {
                  setCurrentServerPage(1);
                  onReFetchData?.();
                }}
                disabled={isLoadingData}
              />
              <ButtonIconBackground
                variant={'search'}
                onClick={handleSearch}
                disabled={isLoadingData}
                title={`${t('buttons:search')} (Enter)`}
              />
            </ContainerButtonStyled>
            {/* UN solo toggle, etiqueta según qué desplegables se vean — mismo
                patrón que `ValidationsTable`, que es como lo resuelven 6 de las
                8 tablas de la casa.
       
                Antes había DOS casillas ("…en Creador" y "…en Operarios") atadas
                al MISMO `includeInactive` del cursor: marcar una marcaba la otra
                y se leían como si fueran independientes. Aparecieron así porque
                el toggle de Creador es anterior a que Ronda 3 añadiera el
                desplegable de Operarios al RV; en vez de pasar a la etiqueta
                combinada se clonó el bloque (y el clon acabó también en
                `CheckBoxTagTable`).
       
                Un solo flag es lo correcto, no un apaño: los dos desplegables
                se alimentan del MISMO `filtersDataOptions.users` y solo cambia
                la rebanada (`filterAndSortMultimediaUsers` para creadores,
                `filterAndSortOperators` para operarios de campo). Nadie quiere
                ver inactivos en uno y ocultarlos en el otro.
       
                Cuando solo se ve un desplegable la etiqueta lo dice (en el RV
                la pestaña Actuaciones oculta Operarios, porque van ligados al
                recorrido). Las tres claves ya existen en ca/es/en. */}
            {(() => {
              const verCreador =
                !isFilterHidden('creator') &&
                !!userOptions &&
                userOptions.length > 0;
              const verOperarios =
                showOperatorsFilter && operatorOptions.length > 0;
              if (!verCreador && !verOperarios) return null;
              const etiqueta =
                verCreador && verOperarios
                  ? 'common:filters.includeInactiveInCreatorAndOperators'
                  : verCreador
                    ? 'common:filters.includeInactiveInCreator'
                    : 'common:filters.includeInactiveInOperators';
              return (
                <IncludeInactiveToggle
                  checked={selectorCursor?.includeInactive ?? false}
                  onChange={onChangeIncludeInactive}
                  label={t(etiqueta)}
                  isDisabled={isLoadingData}
                />
              );
            })()}
            {showOnlyFlaggedFilter && (
              <IncludeInactiveToggle
                checked={Boolean(selectorCursor?.onlyFlagged)}
                onChange={onChangeOnlyFlagged}
                label={flagScopeTexts.onlyFlagged}
                isDisabled={isLoadingData}
              />
            )}
          </SubContainerFiltersStyled>
        )}
      </FiltersWithLoadingStyled>
      <TableContainerStyled isLoading={isLoadingData}>
        <Table {...props} id={idCss}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    {...exportIgnoreProps(header.column.id)}
                    aria-sort={
                      header.column.id === activeSortColumnId
                        ? sortDirection === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </tr>
            ))}
          </thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <DoubleTapRow
                  key={row.id}
                  StyledComponent={TrStyled}
                  componentProps={{
                    checked: row.getIsSelected(),
                    indeterminate: !table.getIsSomeRowsSelected(),
                  }}
                  isDisabled={isLoadingData as boolean}
                  onSingleTap={() => {
                    row.getToggleSelectedHandler()(row);
                  }}
                  onDoubleTap={() => {
                    if (!row.original?.id) return;
                    // V4 (2026-05-27): si el caller pasa onRowDoubleTap,
                    // delega (necesario para SR: navega a
                    // /surveillance-route/:id). Retrocompatible.
                    if (onRowDoubleTap) {
                      onRowDoubleTap(row);
                      return;
                    }
                    navigate(`/work-orders/${row.original.id}`);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      column={cell.column.id as columnID}
                      hiddenColumns={hiddenColumns}
                      key={cell.id}
                      {...exportIgnoreProps(cell.column.id)}
                    >
                      <>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </>
                    </Td>
                  ))}
                </DoubleTapRow>
              );
            })}
          </Tbody>
        </Table>
      </TableContainerStyled>
      {onSelectAll && onClearSelection && (
        <SelectionBanner
          selectedCount={
            isControlled
              ? (propsSelectedCount ?? 0)
              : selectAllMode
                ? (data?.totalCount || 0) - (excludedIds?.size || 0)
                : Object.keys(rowSelection).filter(
                    (k) => (rowSelection as Record<string, boolean>)[k]
                  ).length
          }
          totalCount={data?.totalCount || 0}
          pageCount={dataAll.length}
          allPageSelected={
            dataAll.length > 0 &&
            dataAll.every(
              (row: any) => (rowSelection as Record<string, boolean>)[row.id]
            )
          }
          selectAllMode={selectAllMode}
          onSelectAll={onSelectAll}
          onClearSelection={onClearSelection}
        />
      )}
      <PaginationButtons
        pageCount={Math.max(
          1,
          Math.ceil((data?.totalCount ?? 0) / takePagination)
        )}
        currentPage={currentServerPage}
        nextPage={nextPagination}
        previousPage={backPagination}
        totalCount={data?.totalCount ?? 0}
        isDisabled={isLoadingData}
        // Tarea #11: gates aplicados en `useServerPageSize`.
        {...paginationProps}
      />
    </ContainerStyled>
  );
};
